import { FC, useCallback, useEffect, useRef } from 'react';
import { IWedding_Website_Section_QuestionAnswerField } from '@bridebook/models/source/models/Weddings.types';
import {
  WebsiteTileVariant,
  WeddingWebsiteTileComponent,
} from '@bridebook/web/components/wedding-website/view-mode/partials/tile-component/wedding-website-tile-component';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import { Palettes } from '@bridebook/web/lib/wedding-website/types';
import { FloatingEditButton } from '@bridebook/web/components/wedding-website/view-mode/partials/floating-edit-button/floating-edit-button';
import { shouldShowWebsiteSection } from '@bridebook/web/lib/wedding-website/utils/should-show-website-section';
import { useEditorSyncContext } from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';

interface IProps {
  palette: Palettes;
  variant: WebsiteTileVariant;
  section: IWedding_Website_Section_QuestionAnswerField;
  title: string;
}

const QuestionAnswerSet: FC<{
  sectionData: IWedding_Website_Section_QuestionAnswerField['data'][number];
}> = ({ sectionData }) => (
  <WeddingWebsiteTileComponent.ItemGroup>
    <WeddingWebsiteTileComponent.TextHeading>
      {sectionData.question}
    </WeddingWebsiteTileComponent.TextHeading>
    <WeddingWebsiteTileComponent.Text>{sectionData.answer}</WeddingWebsiteTileComponent.Text>
  </WeddingWebsiteTileComponent.ItemGroup>
);

export const WeddingWebsiteQuestionAnswerField: FC<IProps> = ({
  palette,
  variant,
  section,
  title,
}) => {
  const dispatch = useDispatch();
  const { registerPreviewRef, triggerDetailsSync } = useEditorSyncContext();
  const tileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerPreviewRef('faq', tileRef.current);
  }, [registerPreviewRef]);

  const handleEditQAs = useCallback(() => {
    triggerDetailsSync('faq');
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'faqs',
        element: 'editButton',
      }),
    );
  }, [dispatch, triggerDetailsSync]);
  const questionAnswerSet = section.data;

  if (!shouldShowWebsiteSection(section)) return null;

  return (
    <div ref={tileRef} className="group w-full">
      <WeddingWebsiteTileComponent palette={palette} variant={variant} title={title}>
        <WeddingWebsiteTileComponent.ItemGroup>
          {questionAnswerSet?.map((qa) => <QuestionAnswerSet key={qa.question} sectionData={qa} />)}
        </WeddingWebsiteTileComponent.ItemGroup>
        <FloatingEditButton
          onClick={handleEditQAs}
          href={UrlHelper.website.weddingDetails.sectionAnchor(section.name)}
        />
      </WeddingWebsiteTileComponent>
    </div>
  );
};
